import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { de, UNDO_WINDOW_SECONDS } from '@fsv/core';
import { tx } from '@fsv/data';
import { Icon } from './Icon';
import './Toast.css';

export const UNDO_WINDOW_MS = UNDO_WINDOW_SECONDS * 1000;

interface ToastData {
  id: number;
  message: string;
  tone: 'info' | 'error';
  undo?: (() => Promise<void>) | undefined;
}

interface ToastApi {
  show(message: string, options?: { tone?: 'info' | 'error'; undo?: (() => Promise<void>) | undefined }): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}

/**
 * One toast at a time; a new one replaces the old. With an undo action it
 * stays 10 s (spec section 10) and shows the remaining time as a bar; the
 * timer pauses while the pointer or focus is on it, so nobody loses the
 * button while reaching for it.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const seq = useRef(0);

  const show = useCallback<ToastApi['show']>((message, options) => {
    seq.current += 1;
    setBusy(false);
    setPaused(false);
    setToast({ id: seq.current, message, tone: options?.tone ?? 'info', undo: options?.undo });
  }, []);

  // Remaining time survives a pause: hovering the toast must not restart the 10 s.
  const remaining = useRef(0);
  const startedAt = useRef(0);
  useEffect(() => {
    if (toast) remaining.current = toast.undo ? UNDO_WINDOW_MS : toast.tone === 'error' ? 7000 : 4000;
  }, [toast]);
  useEffect(() => {
    if (!toast || paused) return;
    startedAt.current = Date.now();
    const timer = window.setTimeout(() => setToast((t) => (t?.id === toast.id ? null : t)), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [toast, paused]);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" aria-live="polite" aria-atomic="true">
        {toast && (
          <div
            key={toast.id}
            className={`toast toast--${toast.tone}${paused ? ' toast--paused' : ''}`}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            onPointerEnter={() => setPaused(true)}
            onPointerLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
          >
            <Icon name={toast.tone === 'error' ? 'alert' : 'check'} />
            <span className="toast__message">{toast.message}</span>
            {toast.undo && (
              <button
                type="button"
                className="toast__undo"
                disabled={busy}
                onClick={async () => {
                  const undo = toast.undo;
                  if (!undo) return;
                  setBusy(true);
                  await undo();
                }}
              >
                <Icon name="undo" />
                {de.damage.undo}
              </button>
            )}
            <button type="button" className="toast__close" aria-label={tx.app.close} onClick={() => setToast(null)}>
              <Icon name="close" />
            </button>
            {toast.undo && <span className="toast__timer" style={{ animationDuration: `${UNDO_WINDOW_MS}ms` }} aria-hidden="true" />}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
